// services/apiClient.js - Centralized API service with authorization

import { API_CONFIG, getApiUrl } from '../config/apiconfig';
import { getUserData, clearUserData } from './apiService';
import { logSecurityEvent } from './securityService';
import { recordApiError, recordNetworkError } from './visitTrackingCounters';
import { normalizeGuidsDeep } from '../utils/guidUtils';

class ApiClient {
  constructor() {
    this.baseURL = API_CONFIG.baseURL;
    this.timeout = API_CONFIG.timeout;
    this.isForceLogoutInProgress = false;
  }

  forceLogout(reason = 'session_terminated') {
    if (this.isForceLogoutInProgress) return;
    /* Avoid logout loops while the user is completing MFA on /login. */
    try {
      if (
        typeof window !== 'undefined' &&
        (window.location.pathname === '/login' ||
          sessionStorage.getItem('aq_orgMfaInProgress') === '1')
      ) {
        logSecurityEvent('forced_logout_suppressed', { reason });
        return;
      }
    } catch {
      // continue with logout
    }
    this.isForceLogoutInProgress = true;
    try {
      clearUserData();
      logSecurityEvent('forced_logout', { reason });
    } catch (e) {
      // ignore cleanup errors and continue redirect
    }
    window.location.href = '/login';
  }

  // Get authorization header
  getAuthHeaders() {
    try {
      const userData = getUserData();
      if (!userData || !userData.EmailId) {
        throw new Error('No user session found');
      }

      // Get session key - could be from userData.Sessionkey or APIPWD
      const sessionKey = userData.Sessionkey || userData.APIPWD || '';
      
      if (!sessionKey) {
        throw new Error('No session key found');
      }

      // Create Basic Auth: base64(email:sessionKey)
      const credentials = `${userData.EmailId}:${sessionKey}`;
      const base64Credentials = btoa(credentials);

      const organizationId =
        userData.OrganizationId ||
        userData.Organizationid ||
        userData.organizationId ||
        '';

      const headers = {
        'Authorization': `Basic ${base64Credentials}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };
      /* Active org for session timezone / multi-org email disambiguation */
      if (organizationId) {
        headers['X-Organization-Id'] = String(organizationId);
      }
      return headers;
    } catch (error) {
      console.error('Error creating auth headers:', error);
      return null;
    }
  }

  // Generic API call method
  async makeRequest(url, options = {}) {
    const config = {
      method: 'GET',
      timeout: this.timeout,
      ...options
    };

    try {
      // Add authorization headers for protected endpoints
      if (config.requiresAuth !== false) {
        const authHeaders = this.getAuthHeaders();
        if (!authHeaders) {
          throw new Error('Authentication required but no valid session found');
        }
        config.headers = { ...authHeaders, ...config.headers };
      } else {
        // For public endpoints (like login)
        config.headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...config.headers
        };
      }

      // Setup timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      // Log API call
      logSecurityEvent('api_call', { 
        url: url.replace(this.baseURL, ''), 
        method: config.method,
        requiresAuth: config.requiresAuth !== false
      });

      // Make the request
      const response = await fetch(url, {
        ...config,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      // Handle different response status codes
      if (response.status === 401) {
        logSecurityEvent('unauthorized_api_call', { url });
        // Clear session and redirect to login
        this.forceLogout('unauthorized');
        throw new Error('Unauthorized - Session expired');
      }

      if (response.status === 403) {
        throw new Error('Forbidden - Access denied');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      logSecurityEvent('api_call_success', { 
        url: url.replace(this.baseURL, ''),
        method: config.method 
      });

      return data;

    } catch (error) {
      const isNetworkFailure =
        error?.name === 'AbortError' ||
        (error?.message &&
          (error.message.includes('Failed to fetch') ||
            error.message.includes('NetworkError') ||
            error.message.includes('Request timeout')));

      if (error.name === 'AbortError') {
        recordNetworkError();
      } else if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('NetworkError'))) {
        recordNetworkError();
      } else {
        recordApiError();
      }

      /* Do not force logout on timeouts / network blips — only 401 above ends the session. */

      logSecurityEvent('api_call_error', { 
        url: url.replace(this.baseURL, ''),
        error: error.message 
      });
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  // Convenience methods for different HTTP verbs
  async get(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    return this.makeRequest(url, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    const body = data != null ? normalizeGuidsDeep(data) : data;
    return this.makeRequest(url, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async put(endpoint, data, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    const body = data != null ? normalizeGuidsDeep(data) : data;
    return this.makeRequest(url, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  async delete(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    return this.makeRequest(url, { ...options, method: 'DELETE' });
  }

  async patch(endpoint, data, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;
    const body = data != null ? normalizeGuidsDeep(data) : data;
    return this.makeRequest(url, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  }

  // Public API calls (no auth required)
  async publicGet(endpoint, options = {}) {
    return this.get(endpoint, { ...options, requiresAuth: false });
  }

  async publicPost(endpoint, data, options = {}) {
    return this.post(endpoint, data, { ...options, requiresAuth: false });
  }
}

// Create singleton instance
const apiClient = new ApiClient();

// Export the instance and specific methods
export default apiClient;

// Export individual methods for convenience
export const { get, post, put, delete: del, patch, publicGet, publicPost } = apiClient;